<template>
    <div class="ryanair-process">
        <Asset type="RyanairTransparent" />
        <ButtonLink :onClick="toggleModal">
            {{ t({ key: 'ryanair.booking_process' }) }}
        </ButtonLink>
    </div>

    <Modal
        v-if="modal"
        :onClose="toggleModal"
        :title="t({ key: 'ryanair.booking_process' })"
        size="m">
        <div class="ryanair-process-modal">
            <p>{{ t({ key: 'ryanair.booking_process_description' }) }}</p>

            <ul class="ryanair-process-steps">
                <li
                    v-for="(item, index) in steps"
                    :key="item.step"
                    class="ryanair-process-step">
                    <div v-if="index < steps.length - 1" class="ryanair-process-line" />
                    <span class="ryanair-process-step-number">
                        {{ item.step }}
                    </span>
                    <span class="ryanair-process-step-text">{{ item.label }}</span>
                </li>
            </ul>

            <Asset type="VerifiedByRyanair" />
        </div>

        <template #footer>
            <div class="ryanair-process-footer">
                <ButtonPrimary :onClick="toggleModal">
                    {{ t({ key: 'shared.close', format: LabelFormat.Capitalize }) }}
                </ButtonPrimary>
            </div>
        </template>
    </Modal>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { defineComponent, ref, watch } from 'vue';
    import { LabelFormat } from '@shared/interfaces/translation';
    import Asset from '@ui/components/Asset/Asset.vue';
    import ButtonLink from '@ui/components/Button/ButtonLink.vue';
    import Modal from '@ui/components/Modal/Modal.vue';
    import ButtonPrimary from '@ui/components/Button/ButtonPrimary.vue';
    import { trackClarity } from '@/tracking/clarity';
    import { ClarityEvent } from '@/tracking/clarity/types';
    import { logStatsigCheckoutRyanairInteraction } from '@/tracking/statsig/checkout.functions';

    interface ProcessStep {
        step: number;
        label: string;
    }

    export default defineComponent({
        name: 'RyanairBookingProcess',
        methods: { t },
        components: { Asset, ButtonLink, Modal, ButtonPrimary },
        setup() {
            const modal = ref<boolean>(false);

            const toggleModal = () => {
                modal.value = !modal.value;
            };

            const steps: ProcessStep[] = [
                {
                    step: 1,
                    label: t({ key: 'ryanair.step_pay_booking' }),
                },
                {
                    step: 2,
                    label: t({ key: 'ryanair.step_verify_bookings' }),
                },
                {
                    step: 3,
                    label: t({ key: 'ryanair.step_booking_confirmed' }),
                },
            ];

            watch(
                modal,
                (visible) => {
                    if (visible) {
                        logStatsigCheckoutRyanairInteraction('BookingProcess');
                        trackClarity(ClarityEvent.CHECKOUT_RYANAIR_BOOKING_PROCESS);
                    }
                },
                { immediate: true }
            );

            return {
                modal,
                steps,
                toggleModal,
                LabelFormat,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'RyanairBookingProcess.scss';
</style>
